/**
 * Account-page card for the 4-digit transaction PIN that confirms every
 * money-out action (withdrawals, transfers, investments, repayments).
 *
 *   - No PIN yet  → "Set PIN" modal (new + confirm)
 *   - PIN set     → "Change PIN" (current + new + confirm) and
 *                   "Forgot PIN" (SMS code + new + confirm)
 *
 * Mirrors EmailVerificationCard's card/modal pattern.
 */

import { useState } from "react";
import styled from "styled-components";
import { Button, Input, Modal, message } from "antd";
import { KeypadOutline } from "@raresail/react-ionicons";
import { useAuth } from "../../context/AuthContext";
import useSet from "../../../hooks/common/useSet";
import useGet from "../../../hooks/common/useGet";
import {
  API_PROFILE_ME,
  API_TRANSACTION_PIN_FORGOT,
  API_TRANSACTION_PIN_RESET,
  API_TRANSACTION_PIN_SET,
} from "../../../constants/apis";
import { PRIMARY_COLOR, primary10 } from "../../../constants/colors";

const onlyDigits = (v: string) => v.replace(/\D/g, "");

const PinInput = ({ value, onChange, placeholder }: any) => (
  <Input.Password
    inputMode="numeric"
    maxLength={4}
    value={value}
    onChange={(e) => onChange(onlyDigits(e.target.value))}
    placeholder={placeholder || "••••"}
    style={{ fontSize: 18, letterSpacing: 6, fontWeight: 700 }}
  />
);

export const TransactionPinCard = () => {
  const { user, updateUser } = useAuth();
  const hasPin = !!user?.has_transaction_pin;

  const [mode, setMode] = useState<null | "set" | "change" | "forgot">(null);
  const [current, setCurrent] = useState("");
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [code, setCode] = useState("");

  const { uploadData: savePin, submitting: saving } = useSet();
  const { uploadData: requestReset, submitting: requesting } = useSet();
  const { uploadData: resetPin, submitting: resetting } = useSet();
  const { loadData: refreshMe } = useGet();

  const close = () => {
    setMode(null);
    setCurrent("");
    setPin("");
    setConfirm("");
    setCode("");
  };

  const afterSuccess = () => {
    close();
    refreshMe({
      api: API_PROFILE_ME,
      onSuccess: (me: any) => updateUser(me),
    });
  };

  const validNewPin = () => {
    if (pin.length !== 4) {
      message.error("Your PIN must be exactly 4 digits.");
      return false;
    }
    if (pin !== confirm) {
      message.error("PINs do not match.");
      return false;
    }
    return true;
  };

  const submitSet = () => {
    if (!validNewPin()) return;
    if (mode === "change" && current.length !== 4) {
      message.error("Enter your current PIN.");
      return;
    }
    savePin({
      api: API_TRANSACTION_PIN_SET,
      method: "POST",
      params: {
        pin,
        ...(mode === "change" ? { current_pin: current } : {}),
      },
      successMessage: { message: "Transaction PIN saved", duration: 4 },
      onSuccess: afterSuccess,
    });
  };

  const startForgot = () => {
    requestReset({
      api: API_TRANSACTION_PIN_FORGOT,
      method: "POST",
      params: {},
      successMessage: {
        message: "Reset code sent to your phone",
        duration: 4,
      },
      onSuccess: () => setMode("forgot"),
    });
  };

  const submitForgot = () => {
    if (!code.trim()) {
      message.error("Enter the code we texted you.");
      return;
    }
    if (!validNewPin()) return;
    resetPin({
      api: API_TRANSACTION_PIN_RESET,
      method: "POST",
      params: { code: code.trim(), new_pin: pin },
      successMessage: { message: "Transaction PIN reset", duration: 4 },
      onSuccess: afterSuccess,
    });
  };

  return (
    <>
      <Card>
        <IconBubble>
          <KeypadOutline fontSize={20} color={PRIMARY_COLOR} />
        </IconBubble>
        <div className="flexGrow">
          <div className="font14 semiBold textDark">Transaction PIN</div>
          <div className="font12 grey" style={{ lineHeight: 1.4 }}>
            {hasPin
              ? "Confirms withdrawals, transfers and investments."
              : "Set a 4-digit PIN — required for withdrawals, transfers and investments."}
          </div>
        </div>
        {hasPin ? (
          <div className="flexNullCenter gap5">
            <Button onClick={() => setMode("change")}>Change</Button>
            <Button type="link" loading={requesting} onClick={startForgot}>
              Forgot?
            </Button>
          </div>
        ) : (
          <Button type="primary" onClick={() => setMode("set")}>
            Set PIN
          </Button>
        )}
      </Card>

      {/* Set / change */}
      <Modal
        open={mode === "set" || mode === "change"}
        onCancel={close}
        title={mode === "change" ? "Change transaction PIN" : "Set transaction PIN"}
        footer={null}
        width={400}
      >
        <div className="flexColumn gap10">
          {mode === "change" && (
            <div>
              <div className="font12 grey mb5">Current PIN</div>
              <PinInput value={current} onChange={setCurrent} />
            </div>
          )}
          <div>
            <div className="font12 grey mb5">New 4-digit PIN</div>
            <PinInput value={pin} onChange={setPin} />
          </div>
          <div>
            <div className="font12 grey mb5">Confirm PIN</div>
            <PinInput value={confirm} onChange={setConfirm} />
          </div>
          <Button type="primary" block loading={saving} onClick={submitSet}>
            Save PIN
          </Button>
        </div>
      </Modal>

      {/* Forgot / reset */}
      <Modal
        open={mode === "forgot"}
        onCancel={close}
        title="Reset transaction PIN"
        footer={null}
        width={400}
      >
        <p className="font13 grey" style={{ marginTop: 0 }}>
          We sent a code to <b>{user?.phone_number}</b>.
        </p>
        <div className="flexColumn gap10">
          <div>
            <div className="font12 grey mb5">SMS code</div>
            <Input
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(onlyDigits(e.target.value))}
              placeholder="123456"
            />
          </div>
          <div>
            <div className="font12 grey mb5">New 4-digit PIN</div>
            <PinInput value={pin} onChange={setPin} />
          </div>
          <div>
            <div className="font12 grey mb5">Confirm PIN</div>
            <PinInput value={confirm} onChange={setConfirm} />
          </div>
          <Button type="primary" block loading={resetting} onClick={submitForgot}>
            Reset PIN
          </Button>
        </div>
      </Modal>
    </>
  );
};

const Card = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: #fff;
  border: 1px solid ${primary10};
  border-radius: 12px;
`;

const IconBubble = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: ${primary10};
  display: grid;
  place-items: center;
  flex-shrink: 0;
`;
